import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  CatalogItem,
  fetchItemsData,
} from 'src/utils/CommonDataFetchingUtils/useItems';

interface UseItemReturn {
  item: CatalogItem | null;
  isLoading: boolean;
  error: string | null;
  refetchItem: () => Promise<void>;
}

/* There's no single-item API endpoint — the item's full shape only comes from the
 * catalog list (api/Catalog/Items/{userId}/{role}). So fetch that and find by id,
 * held in local state. Same data source the old redux path used, minus the store.
 * TODO: replace with a real GET api/Catalog/Items/{id} once the backend exposes one. */
export const useItem = (itemId: string): UseItemReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [item, setItem] = useState<CatalogItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const userId: string = user?.id;
  const role: string = user?.roles?.[0]?.role?.name;

  const loadItem = async (): Promise<void> => {
    if (!itemId || !userId || !role) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const items: CatalogItem[] = await fetchItemsData(userId, role);

      const match: CatalogItem | undefined = items.find(
        (candidate: CatalogItem) => candidate.id === itemId
      );

      setItem(match ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadItem();
  }, [itemId, userId, role]);

  return { item, isLoading, error, refetchItem: loadItem };
};

export default useItem;
